import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/layout/Navbar';
import StockTicker from '../components/layout/StockTicker';
import Footer from '../components/layout/Footer';
import { ArticleCardStandard, ArticleCardCompact } from '../components/cards/ArticleCard';
import { Skeleton } from '../components/ui/skeleton';
import { ChevronRight, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryNames = {
  'news': 'News',
  'personal-finance': 'Personal Finance',
  'banking': 'Banking',
  'credit-cards': 'Credit Cards',
  'loans': 'Loans',
  'mortgage': 'Mortgage',
  'retirement': 'Retirement',
  'taxes': 'Taxes',
  'debt-management': 'Debt Management',
  'small-business': 'Small Business',
  'investing': 'Investing',
  'markets': 'Markets',
};

export default function CategoryPage() {
  const { slug, subcategory } = useParams();
  const [articles, setArticles] = useState([]);
  const [trendingArticles, setTrendingArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const limit = 12;

  useEffect(() => {
    setArticles([]);
    setOffset(0);
    setHasMore(true);
    fetchArticles(0, true);
    fetchTrending();
  }, [slug, subcategory]);

  const fetchArticles = async (currentOffset, isInitial = false) => {
    if (isInitial) setLoading(true);
    else setLoadingMore(true);

    try {
      const params = { limit, offset: currentOffset };
      if (subcategory) {
        params.subcategory = subcategory;
      } else {
        params.category = slug;
      }

      const response = await axios.get(`${API}/articles`, { params });
      const newArticles = response.data;

      if (isInitial) {
        setArticles(newArticles);
      } else {
        setArticles((prev) => [...prev, ...newArticles]);
      }

      setHasMore(newArticles.length === limit);
    } catch (error) {
      console.error('Error fetching articles:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const fetchTrending = async () => {
    try {
      const response = await axios.get(`${API}/articles/trending`, { params: { limit: 5 } });
      setTrendingArticles(response.data);
    } catch (error) {
      console.error('Error fetching trending:', error);
    }
  };

  const loadMore = () => {
    const newOffset = offset + limit;
    setOffset(newOffset);
    fetchArticles(newOffset, false);
  };

  const categoryName = categoryNames[subcategory || slug] || slug;
  const parentCategory = subcategory ? categoryNames[slug] : null;

  return (
    <div className="min-h-screen bg-[#020617]" data-testid="category-page">
      <StockTicker />
      <Navbar />

      {/* Header */}
      <header className="py-12 border-b border-[#1E293B]">
        <div className="max-w-7xl mx-auto px-4">
          {parentCategory && (
            <div className="flex items-center text-sm text-slate-500 mb-2 font-mono">
              <span>{parentCategory}</span>
              <ChevronRight className="w-4 h-4 mx-1" strokeWidth={1.5} />
              <span className="text-[#D4AF37]">{categoryName}</span>
            </div>
          )}
          <h1
            className="text-4xl md:text-5xl font-bold text-slate-50"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
            data-testid="category-title"
          >
            {categoryName}
          </h1>
          <p className="text-slate-500 mt-2">
            Browse the latest articles in {categoryName.toLowerCase()}
          </p>
        </div>
      </header>

      {/* Content */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Articles Grid */}
            <div className="lg:col-span-8">
              {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {[...Array(6)].map((_, i) => (
                    <Skeleton key={i} className="w-full h-[300px] rounded-none bg-[#0F172A]" />
                  ))}
                </div>
              ) : articles.length > 0 ? (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {articles.map((article) => (
                      <ArticleCardStandard key={article.id} article={article} />
                    ))}
                  </div>
                  {hasMore && (
                    <div className="mt-8 text-center">
                      <Button
                        onClick={loadMore}
                        disabled={loadingMore}
                        className="bg-[#D4AF37] text-[#020617] hover:bg-[#B8941F] font-bold uppercase tracking-widest px-8 rounded-none"
                        data-testid="load-more-btn"
                      >
                        {loadingMore ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Loading...
                          </>
                        ) : (
                          'Load More'
                        )}
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-20">
                  <p className="text-slate-500">No articles found in this category.</p>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <aside className="lg:col-span-4">
              <div className="sidebar-widget p-6 rounded-none sticky top-24">
                <h3
                  className="text-lg font-bold text-slate-100 mb-4"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  Trending Now
                </h3>
                <div className="space-y-0">
                  {loading ? (
                    [...Array(5)].map((_, i) => (
                      <Skeleton key={i} className="w-full h-[60px] mb-4 rounded-none bg-[#1E293B]" />
                    ))
                  ) : trendingArticles.length > 0 ? (
                    trendingArticles.map((article, index) => (
                      <ArticleCardCompact key={article.id} article={article} index={index} />
                    ))
                  ) : articles.slice(0, 5).map((article, index) => (
                    <ArticleCardCompact key={article.id} article={article} index={index} />
                  ))}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
